import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, BillingBadge, useList } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import TemplatePreview from "@/components/TemplatePreview";
import { FormField } from "@/pages/Contacts";
import { useChannels } from "@/hooks/useChannels";
import { FileText, Plus, PaperPlaneTilt, CircleNotch, Prohibit, PlusCircle, Trash, ArrowRight, ArrowLeft, CheckCircle, TextT, Image as ImageIcon, ListBullets, Cards } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  channel_id: "", name: "", language: "id", category: "UTILITY",
  header: { enabled: false, format: "TEXT", text: "", link: "" },
  body: "Halo {{1}}, terima kasih telah menghubungi kami.",
  footer: { enabled: false, text: "" },
  buttons: [],
};

const STEPS = [
  { label: "Detail", icon: Cards },
  { label: "Header", icon: ImageIcon },
  { label: "Isi Pesan", icon: TextT },
  { label: "Tombol", icon: ListBullets },
  { label: "Tinjau", icon: CheckCircle },
];

const CATEGORIES = [
  { value: "UTILITY", label: "Utility", desc: "Notifikasi transaksi & layanan." },
  { value: "MARKETING", label: "Marketing", desc: "Promosi, penawaran, kabar produk." },
  { value: "AUTHENTICATION", label: "Authentication", desc: "Kode OTP & verifikasi." },
];

function parseComponents(t) {
  const f = JSON.parse(JSON.stringify(EMPTY));
  f.channel_id = t.channel_id; f.name = t.name; f.language = t.language; f.category = t.category || "UTILITY";
  (t.components || []).forEach((c) => {
    if (c.type === "HEADER") f.header = { enabled: true, format: c.format || "TEXT", text: c.text || "", link: c.example?.link || "" };
    if (c.type === "BODY") f.body = c.text || "";
    if (c.type === "FOOTER") f.footer = { enabled: true, text: c.text || "" };
    if (c.type === "BUTTONS") f.buttons = (c.buttons || []).map((b) => ({ type: b.type, text: b.text || "", url: b.url || "", phone_number: b.phone_number || "" }));
  });
  return f;
}
function buildComponents(f) {
  const comps = [];
  if (f.header.enabled) {
    if (f.header.format === "TEXT") comps.push({ type: "HEADER", format: "TEXT", text: f.header.text });
    else comps.push({ type: "HEADER", format: f.header.format, example: { link: f.header.link } });
  }
  comps.push({ type: "BODY", text: f.body });
  if (f.footer.enabled && f.footer.text) comps.push({ type: "FOOTER", text: f.footer.text });
  if (f.buttons.length) comps.push({ type: "BUTTONS", buttons: f.buttons.map((b) => ({ type: b.type, text: b.text, ...(b.type === "url" ? { url: b.url } : {}), ...(b.type === "phone_number" ? { phone_number: b.phone_number } : {}) })) });
  return comps;
}
function previewComponents(f) {
  const comps = [];
  if (f.header.enabled) comps.push({ type: "HEADER", format: f.header.format, text: f.header.text });
  comps.push({ type: "BODY", text: f.body });
  if (f.footer.enabled) comps.push({ type: "FOOTER", text: f.footer.text });
  if (f.buttons.length) comps.push({ type: "BUTTONS", buttons: f.buttons });
  return comps;
}
// All template-category messages are billable (Berbayar); only free-form session replies are free.
const billingOf = () => "paid";

function Stepper({ step, setStep, editable }) {
  return (
    <div className="flex items-center gap-1 mb-8">
      {STEPS.map((s, i) => {
        const n = i + 1;
        const done = n < step;
        const active = n === step;
        return (
          <div key={s.label} className="flex items-center flex-1 last:flex-none">
            <button type="button" onClick={() => editable && setStep(n)} data-testid={`step-tab-${n}`} className={`flex items-center gap-2 ${editable ? "cursor-pointer" : "cursor-default"}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                active ? "bg-[#003399] text-white" : done ? "bg-[#0A8A00] text-white" : "bg-[#F5F5F5] text-[#767676]"
              }`}>
                {done ? <CheckCircle className="w-4 h-4" weight="regular" /> : <s.icon className="w-4 h-4" weight="regular" />}
              </span>
              <span className={`text-[13px] font-semibold hidden xl:inline ${active ? "text-[#003399]" : "text-slate-400"}`}>{s.label}</span>
            </button>
            {n < STEPS.length && <div className={`h-[2px] flex-1 mx-2 rounded-full ${done ? "bg-[#0A8A00]" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function Templates() {
  const channels = useChannels();
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch } = useList("/templates");
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);

  const channelName = (id) => channels.find((c) => c.id === id)?.name || "-";
  const editable = !editing || ["DRAFT", "REJECTED"].includes(editing.status);

  const openCreate = () => { setForm(JSON.parse(JSON.stringify(EMPTY))); setEditing(null); setStep(1); setDrawer(true); };
  const openDetail = (t) => { setEditing(t); setForm(parseComponents(t)); setStep(1); setDrawer(true); };

  const setH = (patch) => setForm((f) => ({ ...f, header: { ...f.header, ...patch } }));
  const setF = (patch) => setForm((f) => ({ ...f, footer: { ...f.footer, ...patch } }));
  const addButton = () => setForm((f) => ({ ...f, buttons: [...f.buttons, { type: "quick_reply", text: "", url: "", phone_number: "" }] }));
  const updateButton = (i, patch) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, idx) => idx === i ? { ...b, ...patch } : b) }));
  const removeButton = (i) => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));

  const canNext = () => {
    if (step === 1) return (editing || form.channel_id) && form.name && form.language;
    if (step === 3) return form.body.trim().length > 0;
    if (step === 4) return form.buttons.every((b) => b.text.trim() && (b.type !== "url" || b.url.trim()) && (b.type !== "phone_number" || b.phone_number.trim()));
    return true;
  };

  const save = async () => {
    setSaving(true);
    try {
      const components = buildComponents(form);
      if (editing) {
        await api.put(`/templates/${editing.id}`, { name: form.name, language: form.language, category: form.category, components });
        toast.success("Template diperbarui.");
      } else {
        await api.post("/templates", { channel_id: form.channel_id, name: form.name, language: form.language, category: form.category, components });
        toast.success("Template dibuat sebagai DRAFT.");
      }
      setDrawer(false); refetch();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };
  const submitReview = async () => { try { await api.post(`/templates/${editing.id}/submit`, {}); toast.success("Template diajukan untuk review."); setDrawer(false); refetch(); } catch (e) { toast.error(apiErrorMessage(e)); } };
  const disable = async () => { if (!window.confirm(`Nonaktifkan template ${editing.name}?`)) return; try { await api.del(`/templates/${editing.id}`); toast.success("Template dinonaktifkan."); setDrawer(false); refetch(); } catch (e) { toast.error(apiErrorMessage(e)); } };

  return (
    <div>
      <PageHeader title="Template" subtitle="Bangun template pesan WhatsApp langkah demi langkah."
        action={<Button onClick={openCreate} className="bg-[#FFDA1A] hover:bg-[#F5CE00] text-[#111111] h-[42px]" data-testid="create-template-button"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> Template Baru</Button>} />

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari nama template..." testid="template-search" />
        <Select value={filters.channel_id || "all"} onValueChange={(v) => setFilters({ ...filters, channel_id: v === "all" ? "" : v })}>
          <SelectTrigger className="w-44 bg-white h-[42px]" data-testid="template-channel-filter"><SelectValue placeholder="Semua channel" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua channel</SelectItem>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white h-[42px]" data-testid="template-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua status</SelectItem>{["DRAFT", "PENDING", "APPROVED", "REJECTED", "DISABLED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={FileText} title="Belum ada template" description="Bangun template pesan, ajukan review, dan pakai saat compose message."
          action={<Button onClick={openCreate} className="bg-[#003399] hover:bg-[#002B80]"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> Template Baru</Button>} />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="text-[11px] uppercase tracking-[0.05em] bg-[#F5F5F5] text-[#484848] font-semibold border-b border-[#DFDFDF]">
              <tr><th className="px-6 py-3.5">Nama</th><th className="px-6 py-3.5 hidden md:table-cell">Channel</th><th className="px-6 py-3.5 hidden lg:table-cell">Bahasa</th><th className="px-6 py-3.5 hidden lg:table-cell">Kategori</th><th className="px-6 py-3.5">Biaya</th><th className="px-6 py-3.5">Status</th></tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr key={t.id} onClick={() => openDetail(t)} className="border-b border-[#F5F5F5] last:border-0 hover:bg-[#F5F5F5] transition-colors cursor-pointer group" data-testid={`template-row-${t.id}`}>
                  <td className="px-6 py-3.5 font-mono text-[13px] font-medium text-[#003399] border-l-2 border-transparent group-hover:border-[#FFDA1A]">{t.name}</td>
                  <td className="px-6 py-3.5 hidden md:table-cell text-[#484848]">{channelName(t.channel_id)}</td>
                  <td className="px-6 py-3.5 hidden lg:table-cell text-[#484848]">{t.language}</td>
                  <td className="px-6 py-3.5 hidden lg:table-cell text-[#484848]">{t.category || "-"}</td>
                  <td className="px-6 py-3.5"><BillingBadge billing={billingOf(t.category)} /></td>
                  <td className="px-6 py-3.5"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={drawer} onClose={() => setDrawer(false)} width="max-w-3xl"
        title={editing ? `Template: ${editing.name}` : "Buat Template"}
        description={editing ? `Status: ${editing.status}` : "Ikuti langkah-langkah untuk membangun template."} testid="template-drawer"
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2">
              {editing && editing.status === "DRAFT" && <Button variant="outline" onClick={submitReview} data-testid="template-submit-review"><PaperPlaneTilt className="w-4 h-4 mr-1.5" weight="regular" /> Ajukan Review</Button>}
              {editing && editing.status !== "DISABLED" && <Button variant="outline" className="text-[#CC0008] border-[#CC000833] hover:bg-[#CC000810]" onClick={disable} data-testid="template-disable"><Prohibit className="w-4 h-4 mr-1.5" weight="bold" /> Nonaktifkan</Button>}
            </div>
            <div className="flex gap-2">
              {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wizard-back"><ArrowLeft className="w-4 h-4 mr-1.5" weight="bold" /> Kembali</Button>}
              {step < STEPS.length && <Button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} className="bg-[#003399] hover:bg-[#002B80]" data-testid="wizard-next">Lanjut <ArrowRight className="w-4 h-4 ml-1.5" weight="bold" /></Button>}
              {step === STEPS.length && editable && <Button onClick={save} disabled={saving} className="bg-[#FFDA1A] hover:bg-[#F5CE00] text-[#111111]" data-testid="template-save">{saving ? <CircleNotch className="w-4 h-4 animate-spin" weight="bold" /> : "Simpan Template"}</Button>}
            </div>
          </div>
        }>
        <Stepper step={step} setStep={setStep} editable={editable} />
        {editing && !editable && (
          <div className="mb-5 p-3 rounded-lg bg-[#E8740010] text-sm text-[#8A4500]">
            Template berstatus <b>{editing.status}</b> tidak dapat diedit. Hanya DRAFT & REJECTED yang bisa diubah.
          </div>
        )}

        {step < 5 ? (
          <fieldset disabled={!editable} className="disabled:opacity-70 max-w-2xl">
            <div className="animate-step-in" key={step}>
              {step === 1 && (
                <div className="space-y-5">
                  {editing && editing.status === "REJECTED" && editing.rejection_reason && (
                    <div className="p-3 rounded-lg bg-[#CC000810] text-sm text-[#CC0008]" data-testid="template-rejection-reason">
                      <span className="font-semibold">Alasan penolakan:</span> {editing.rejection_reason} <span className="block mt-1 text-xs">Perbarui lalu simpan untuk kembali ke DRAFT.</span>
                    </div>
                  )}
                  {!editing && (
                    <div className="space-y-2">
                      <Label>Channel Tujuan *</Label>
                      <Select value={form.channel_id} onValueChange={(v) => setForm({ ...form, channel_id: v })}>
                        <SelectTrigger data-testid="template-form-channel"><SelectValue placeholder="Pilih channel WhatsApp" /></SelectTrigger>
                        <SelectContent>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Kategori Pesan *</Label>
                    <div className="space-y-2.5">
                      {CATEGORIES.map((c) => (
                        <button type="button" key={c.value} onClick={() => setForm({ ...form, category: c.value })} data-testid={`category-${c.value}`}
                          className={`w-full text-left p-3.5 rounded-[4px] border-2 flex items-center gap-3 transition-all ${form.category === c.value ? "border-[#003399] bg-[#00339908]" : "border-transparent bg-[#F5F5F5] hover:bg-[#ECECEC]"}`}>
                          <span className={`w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 ${form.category === c.value ? "border-[#003399]" : "border-slate-300"} flex items-center justify-center`}>
                            {form.category === c.value && <span className="w-2 h-2 rounded-full bg-[#003399]" />}
                          </span>
                          <span className="flex-1">
                            <span className="flex items-center gap-2"><span className="font-semibold text-sm text-[#003399]">{c.label}</span><BillingBadge billing="paid" /></span>
                            <span className="text-xs text-[#484848]">{c.desc}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Nama Template *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="order_confirmation" testid="template-form-name" />
                    <FormField label="Bahasa *" value={form.language} onChange={(v) => setForm({ ...form, language: v })} placeholder="id" testid="template-form-language" />
                  </div>
                  <p className="text-xs text-[#484848]">Nama hanya boleh huruf kecil, angka, dan underscore.</p>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between p-4 rounded-[4px] bg-[#F5F5F5]">
                    <div><Label>Aktifkan Header</Label><p className="text-xs text-[#484848] mt-0.5">Media atau teks di atas pesan (opsional).</p></div>
                    <Switch checked={form.header.enabled} onCheckedChange={(v) => setH({ enabled: v })} data-testid="template-header-toggle" />
                  </div>
                  {form.header.enabled && (
                    <div className="space-y-3 animate-step-in">
                      <div className="space-y-2">
                        <Label>Format Header</Label>
                        <Select value={form.header.format} onValueChange={(v) => setH({ format: v })}>
                          <SelectTrigger data-testid="template-header-format"><SelectValue /></SelectTrigger>
                          <SelectContent>{["TEXT", "IMAGE", "VIDEO", "DOCUMENT"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {form.header.format === "TEXT"
                        ? <div className="space-y-2"><Label>Teks Header</Label><Input value={form.header.text} onChange={(e) => setH({ text: e.target.value })} placeholder="Maks 60 karakter" maxLength={60} data-testid="template-header-text" /></div>
                        : <div className="space-y-2"><Label>URL Contoh Media</Label><Input value={form.header.link} onChange={(e) => setH({ link: e.target.value })} placeholder="https://..." data-testid="template-header-link" /></div>}
                    </div>
                  )}
                </div>
              )}
              {step === 3 && (
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label>Isi Body * <span className="text-xs text-[#767676] font-normal">gunakan {"{{1}}"} untuk parameter</span></Label>
                    <Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="template-form-body" />
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-[4px] bg-[#F5F5F5]">
                    <div><Label>Aktifkan Footer</Label><p className="text-xs text-[#484848] mt-0.5">Teks kecil di bawah pesan (opsional).</p></div>
                    <Switch checked={form.footer.enabled} onCheckedChange={(v) => setF({ enabled: v })} data-testid="template-footer-toggle" />
                  </div>
                  {form.footer.enabled && <Input value={form.footer.text} onChange={(e) => setF({ text: e.target.value })} placeholder="Maks 60 karakter" maxLength={60} data-testid="template-footer-text" />}
                </div>
              )}
              {step === 4 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div><Label>Tombol Aksi</Label><p className="text-xs text-[#484848] mt-0.5">Tambahkan tombol interaktif (opsional).</p></div>
                    <Button type="button" size="sm" variant="outline" onClick={addButton} data-testid="template-add-button"><PlusCircle className="w-4 h-4 mr-1" weight="regular" /> Tambah</Button>
                  </div>
                  <div className="space-y-2">
                    {form.buttons.map((b, i) => (
                      <div key={i} className="flex gap-2 items-center p-2 rounded-[4px] bg-[#F5F5F5]" data-testid={`template-button-${i}`}>
                        <Select value={b.type} onValueChange={(v) => updateButton(i, { type: v })}>
                          <SelectTrigger className="w-32 flex-shrink-0" data-testid={`template-button-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{["quick_reply", "url", "catalog", "flow", "phone_number"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={b.text} onChange={(e) => updateButton(i, { text: e.target.value })} placeholder="Teks tombol" data-testid={`template-button-text-${i}`} />
                        {b.type === "url" && <Input value={b.url} onChange={(e) => updateButton(i, { url: e.target.value })} placeholder="https://..." data-testid={`template-button-url-${i}`} />}
                        {b.type === "phone_number" && <Input value={b.phone_number} onChange={(e) => updateButton(i, { phone_number: e.target.value })} placeholder="+628123456789" data-testid={`template-button-phone-${i}`} />}
                        <button type="button" onClick={() => removeButton(i)} className="text-slate-400 hover:text-[#CC0008] flex-shrink-0" data-testid={`template-button-remove-${i}`}><Trash className="w-4 h-4" weight="regular" /></button>
                      </div>
                    ))}
                    {form.buttons.length === 0 && <p className="text-sm text-[#767676] py-6 text-center border border-dashed border-[#DFDFDF] rounded-[4px]">Belum ada tombol.</p>}
                  </div>
                </div>
              )}
            </div>
          </fieldset>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-step-in">
            <div className="space-y-4">
              <h4 className="font-heading font-semibold text-[#003399]">Tinjau Template</h4>
              <div className="rounded-[4px] bg-[#F5F5F5] divide-y divide-[#E8E8E8] overflow-hidden">
                <ReviewRow label="Channel" value={channelName(form.channel_id) || (editing && channelName(editing.channel_id))} />
                <ReviewRow label="Nama" value={form.name} mono />
                <ReviewRow label="Bahasa" value={form.language} />
                <ReviewRow label="Kategori" value={form.category} />
                <div className="flex items-center justify-between px-4 py-3 bg-[#F5F5F5]">
                  <span className="text-[13px] text-[#484848]">Klasifikasi Biaya</span>
                  <BillingBadge billing="paid" />
                </div>
              </div>
              <p className="text-xs text-[#484848]">{editing ? "Perubahan akan disimpan." : "Template baru disimpan sebagai DRAFT."} Ajukan review setelah disimpan.</p>
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-[0.05em] font-semibold text-[#484848] mb-2 block">Preview WhatsApp</span>
              <TemplatePreview components={previewComponents(form)} />
            </div>
          </div>
        )}
      </RightDrawer>
    </div>
  );
}

const ReviewRow = ({ label, value, mono }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <span className="text-[13px] text-[#484848]">{label}</span>
    <span className={`text-[13px] font-medium text-[#003399] ${mono ? "font-mono" : ""}`}>{value || "-"}</span>
  </div>
);
